import { Component, EventEmitter, Input, Output, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';


// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';

// Application Services
import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

import { UserAdditionalName } from '../../../../../models/user.model';

// Interface for an individual additional name entry.
// Added RecID to track existing records from the backend.
export interface AdditionalName {
	id: string;
	recId?: number; // Optional, indicates if it's an existing record
	name: string;
	description: string;
	isActive: number;
	isApproved: number;
}

@Component({
	selector: 'app-additional-names',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		InputTextModule,
		ButtonModule,
		CardModule,
		TagModule,
		ToastModule,
		TableModule,
		TooltipModule],
	templateUrl: './additional-names.component.html',
	styleUrls: ['./additional-names.component.scss'],
	providers: [MessageService]
})
export class AdditionalNamesComponent implements OnInit {
	// Component Inputs & Outputs
	@Input() additionalNames: AdditionalName[] = [];
	@Output() additionalNamesChange = new EventEmitter<AdditionalName[]>();
	@Output() nextStep = new EventEmitter<void>();
	@Output() prevStep = new EventEmitter<void>();
	@Output() saveAndClose = new EventEmitter<AdditionalName[]>();
	@Output() skipToLastStep = new EventEmitter<void>();


	@Input() customerRecId!: number;

	// Constants
	readonly DESCRIPTION_MAX_LENGTH = 250;

	// Component State
	isDialogMode = false;
	isCreate = false;
	newEntry: AdditionalName = { id: '', name: '', description: '', isActive: 1, isApproved: 1 };

	// Constructor with @Optional() to prevent injection errors
	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		public dirServ: DirectionService,
		private viewScopeService: ViewScopeService,
		// The @Optional() decorator tells Angular to inject 'null' if these providers are not found.
		// This is necessary for a component used in both a dialog and as a standalone component.
		@Optional() public ref: DynamicDialogRef,
		@Optional() public config: DynamicDialogConfig,
		@Optional() private maintainAccountService: MaintainAccountService
	) { }

	ngOnInit(): void {
		if (this.config && this.config.data) {
			this.isDialogMode = this.config.data.isDialog;
			this.customerRecId = this.config.data.customerRecId;
			this.isCreate = this.config.data.isCreate;

			if (!this.isCreate && this.config.data.nameEntry) {
				// Edit mode: initialize with existing record(s)
				this.additionalNames = [this.config.data.nameEntry];
			} else {
				// Create mode: do NOT pre-push a row here.
				// The footer row with newEntry will handle input.
				this.additionalNames = [];
			}
		}
		else {
			this.isDialogMode = false;
			this.isCreate = true;

			// Ensure all existing items have a unique ID for tracking.
			this.additionalNames.forEach(item => {
				if (!item.id) {
					item.id = this.generateUniqueId();
				}
			});
			this.resetNewEntry();
		}
	}



	private generateUniqueId(): string {
		return 'id_' + Math.random().toString(36).substring(2, 9);
	}

	resetNewEntry(): void {
		this.newEntry = {
			id: this.generateUniqueId(),
			name: '',
			description: '',
			isActive: 1,      // default active
			isApproved: 1     // default approved
		};
	}

	addEntry(): void {
		// Validation logic for adding a new entry
		if (!this.newEntry.name.trim()) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Name for the new entry is required.',
				life: 3000
			});
			return;
		}
		if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
				life: 3000
			});
			return;
		}

		// Add the new entry and reset the form
		this.additionalNames.push({ ...this.newEntry });
		this.emitChanges();
		if (this.isCreate) {
			this.resetNewEntry();
		}

		this.messageService.add({
			severity: 'success',
			summary: 'Added',
			detail: 'New additional name added.',
			life: 1500
		});
	}

	removeEntry(entryToRemove: AdditionalName): void {
		this.additionalNames = this.additionalNames.filter(entry => entry.id !== entryToRemove.id);
		this.emitChanges();
		this.messageService.add({
			severity: 'info',
			summary: 'Removed',
			detail: 'Additional name removed.',
			life: 1500
		});
	}

	emitChanges(): void {
		this.additionalNamesChange.emit(this.additionalNames);
	}

	getRemainingChars(description?: string): number {
		return this.DESCRIPTION_MAX_LENGTH - (description?.length ?? 0);
	}


	/**
	 * This function is intended for the stepper's "next" button.
	 * It validates the form and emits the `nextStep` event.
	 * It does NOT handle saving or closing the dialog.
	 */
	validateAndProceed(): void {
		let isValid = true;
		for (const entry of this.additionalNames) {
			if (!entry.name.trim()) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Name for entry "${entry.id}" is required.`,
					life: 3000
				});
				break;
			}
			if ((entry.description?.length ?? 0) > this.DESCRIPTION_MAX_LENGTH) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Description for "${entry.name}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
					life: 3000
				});
				break;
			}

		}

		if (isValid) {
			// Emits the event to move to the next step
			this.nextStep.emit();
			this.messageService.clear();
		}
	}



	goToPreviousStep(): void {
		this.prevStep.emit();
	}


	applyRowChanges(entry: AdditionalName): void {
		// Simply emit the changes so parent knows data is dirty
		this.emitChanges();

		this.messageService.add({
			severity: 'info',
			summary: 'Changes Applied',
			detail: `Changes applied for "${entry.name}". Remember to press Save to persist.`,
			life: 2500
		});
	}



	/**
	 * This function is intended for the dialog's "save" button.
	 * It validates the form and then calls the API to save/update the data.
	 * It uses forkJoin to wait for all API calls to complete before closing the dialog.
	 */
	saveAndSubmit(): void {
		// First, run validation
		let isValid = true;

		for (const entry of this.additionalNames) {
			if (!entry.name.trim()) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Name for entry "${entry.id}" is required.`,
					life: 3000
				});
				break;
			}
			if ((entry.description?.length ?? 0) > this.DESCRIPTION_MAX_LENGTH) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Description for "${entry.name}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
					life: 3000
				});
				break;
			}
		}

		if (!isValid) return;

		// Only attempt API calls if the service is available
		if (this.maintainAccountService) {
			const requests = this.additionalNames.map(entry => {
				const basePayload: any = {
					AdditName: entry.name,
					Descr: entry.description || '',   // safe for empty
					IsActive: entry.isActive,
					IsApproved: entry.isApproved,
					UserRecID: this.viewScopeService.getCurrentOpertaingUserId()!
				};

				if (this.isCreate) {
					// For create → use CustomerRecID
					basePayload.CustomerRecID = this.customerRecId;
				} else {
					// For edit → use RecID (taken from the entry provided by tab screen)
					basePayload.RecID = entry.recId;
				}

				return basePayload;
			});


			console.log("Data we will save:", requests);

			// Call API for each record separately
			requests.forEach(record => {
				this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record)).subscribe(
					{
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: 'Success',
								detail: `Saved record for "${record.AdditName}".`,
								life: 2000
							});

							// Close dialog only after the last record
							if (this.ref && record === requests[requests.length - 1]) {
								this.ref.close(response);
							}
						},
						error: (error) => {
							this.messageService.add({
								severity: 'error',
								summary: 'Error',
								detail: `Failed to save record for "${record.AdditName}".`,
								life: 3000
							});
						},
						complete: () => {
							// This block runs after all API calls are complete
							this.messageService.add({
								severity: 'success',
								summary: 'Complete',
								detail: 'All records processed.',
								life: 2000
							});
							// Reload data from API after successful submission

							//this.loadDataFromApi();


							// Close dialog
							if (this.ref) {
								this.ref.close(true);
							}
						}
					});
			});

		} else {
			this.messageService.add({
				severity: 'warn',
				summary: 'Warning',
				detail: 'This functionality is not available in standalone mode.',
				life: 3000
			});
		}
	}


	private mapUserToAdditional(userName: UserAdditionalName): AdditionalName {
		return {
			id: this.generateUniqueId(),
			recId: userName.RecID,
			name: userName.CustomerName || '',
			description: userName.Descr || '',
			isActive: userName.IsActive,
			isApproved: userName.IsApproved,
		};
	}

	getSeverity(val: boolean): string {
		return val ? 'success' : 'danger';
	}

	// loadDataFromApi is now called by the parent component after dialog close
	// this method is not needed inside this component anymore.
	// It is kept for potential future use or debugging.
	loadDataFromApi(): void {
		this.additionalNames = [];

		if (this.maintainAccountService && this.customerRecId) {
			const index = this.maintainAccountService.getMappingIndex("additionalNames");
			const instance = this.maintainAccountService.getUserDataMappings();
			this.maintainAccountService.getDatas<UserAdditionalName>(
				this.customerRecId,
				instance[index].apiName,
				instance[index].special
			).subscribe({
				next: (data) => {  // data is UserAdditionalName[]
					this.additionalNames = data.map(item => this.mapUserToAdditional(item));
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'Data reloaded successfully.',
						life: 1500
					});
					this.emitChanges();
				},
				error: (err: any) => {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to load data from API.',
						life: 3000
					});
				}
			});

		}
	}

	toggleIsActive(entry: any): void {
		entry.isActive = entry.isActive === 1 ? 0 : 1;
	}

	toggleIsApproved(entry: any): void {
		entry.isApproved = entry.isApproved === 1 ? 0 : 1;
	}


	closeDialog(shouldReload: boolean = false): void {
		if (this.ref) {
			this.ref.close(shouldReload);
		}
	}
}
